import type React from "react";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { NavLink, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { TOAST_MESSAGES } from "~/constants/messages";
import { AuthApi } from "~/functions/api/auth";
import { OrganizationApi } from "~/functions/api/organization";
import ExternalLinkIcon from "~/images/external-site.svg?react";
import type { Organization } from "~/interface/organization";
import { setIntendedUrl } from "~/slices/auth";
import type { RootState } from "~/store";
import { OrganizationSelectionModal } from "../../Organization/organizationSelectionModal/OrganizationSelectionModal";
import styles from "./index.module.scss";

interface UserDropdownProps {
  isOpen: boolean;
  onClose: () => void;
  onStartTutorial?: () => void;
}

export const UserDropdown: React.FC<UserDropdownProps> = ({ isOpen, onClose, onStartTutorial }) => {
  const dropdownRef = useRef<HTMLDivElement>(null);
  const dispatch = useDispatch();
  const location = useLocation();
  const { currentUser, currentUserInfo } = useSelector((state: RootState) => state.auth);
  const [showOrgModal, setShowOrgModal] = useState<boolean>(false);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [organizationsLoaded, setOrganizationsLoaded] = useState<boolean>(false);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [onClose]);

  // 組織のデータをロードする関数
  const loadOrganizations = async () => {
    if (!currentUserInfo || !currentUserInfo.user_data) {
      toast.error(TOAST_MESSAGES.LAYOUT.FETCH_USER_FAILED);
      return false;
    }

    try {
      const organizationApi = new OrganizationApi();
      const orgs: Organization[] = [];

      for (const data of currentUserInfo.user_data) {
        try {
          const org = await organizationApi.get(data.organization_id);
          if (org) {
            orgs.push(org);
          }
        } catch (err) {
          console.error(`組織ID ${data.organization_id} の取得に失敗:`, err);
        }
      }

      if (orgs.length > 0) {
        setOrganizations(orgs);
        setOrganizationsLoaded(true);
        return true;
      } else {
        toast.error(TOAST_MESSAGES.LAYOUT.FETCH_ORGANIZATION_FAILED);
        return false;
      }
    } catch (_error) {
      toast.error(TOAST_MESSAGES.LAYOUT.FETCH_ORGANIZATION_ERROR);
      return false;
    }
  };

  const handleLogout = async () => {
    const authApi = new AuthApi();

    try {
      const response = await authApi.signout();
      if (response !== null) {
        const data = await response.json();
        const redirectUrl = data.redirectUrl;
        if (redirectUrl) {
          // toast.success("ログアウトに成功"); // 画面遷移で明らかなため削除
          setTimeout(() => {
            window.location.href = redirectUrl;
          }, 1000);
        }
      }
    } catch (_error) {
      toast.error(TOAST_MESSAGES.AUTH.SIGNOUT_FAILED);
    }
  };

  const handleShowOrganizationModal = async () => {
    // 現在のURLを保存
    const currentPath = location.pathname + location.search + location.hash;
    dispatch(setIntendedUrl(currentPath));

    // 組織データがまだロードされていない場合、または再ロードする場合
    if (!organizationsLoaded || organizations.length === 0) {
      const success = await loadOrganizations();
      if (!success) return;
    }

    setShowOrgModal(true);
    onClose(); // Close the dropdown menu
  };

  const handleCloseOrgModal = () => {
    setShowOrgModal(false);
  };

  // このコンポーネントは常にレンダリングし、表示/非表示のみを切り替える
  return (
    <>
      {isOpen && (
        <div className={styles["dropdownMenu"]} ref={dropdownRef}>
          <ul>
            <li>
              <NavLink to="#" onClick={handleShowOrganizationModal}>
                組織選択
              </NavLink>
            </li>
            <li>
              <a href="https://www.reqxign.net/contact" target="_blank" rel="noreferrer">
                <div className="flex gap-2 items-center">
                  お問い合わせ <ExternalLinkIcon />
                </div>
              </a>
            </li>
            <li>
              <NavLink
                to={`/${currentUser.organization_id}/user-setting`}
                data-testid="nav-user-settings"
              >
                アカウント設定
              </NavLink>
            </li>
            {onStartTutorial && (
              <li>
                <NavLink
                  to="#"
                  onClick={(e) => {
                    e.preventDefault();
                    onStartTutorial();
                    onClose();
                  }}
                >
                  チュートリアルを見る
                </NavLink>
              </li>
            )}
            <li>
              <NavLink to="#" onClick={handleLogout} data-testid="logout-button">
                ログアウト
              </NavLink>
            </li>
          </ul>
        </div>
      )}
      <OrganizationSelectionModal
        isOpen={showOrgModal}
        organizations={organizations}
        onClose={handleCloseOrgModal}
      />
    </>
  );
};
